import React, { useState, useEffect } from 'react';
import { createMarkup } from '../../../../../../Foundation/React/code/Utilities/HelperUtilities.js';
import { fetchPromise } from './apiHelper.js';
import Orderline from './Orderline.js';
import Checkbox from '../../../../../../Foundation/Common/code/Views/Foundation/Common/Checkbox.js';
import Notification from '../../../../../../Foundation/Common/code/Views/Foundation/Common/Notifications/Notification.js';
import Spinner from '../../../../../../Foundation/Common/code/Views/Foundation/Common/Spinner.js';
import OrderDetails from './OrderDetails.js';

export default (props) => {
  const { Translations, currentOrder, showHomeDeliveryDesc } = props;
  const [returnCompleted, setReturnCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDisclaimerError, setShowDisclaimerError] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [orderLines, setOrderLines] = useState(currentOrder.OrderLines);
  const [returnData, setReturnData] = useState([]);
  const itemsToReturn = orderLines.filter((x) => x.QuantityToReturn > 0);
  const [returnDescription, setReturnDescription] = useState();
  const infoMessage = currentOrder.Messages?.find((m) => m.Type === 2)?.Content;

  useEffect(() => {
    if (disclaimerAccepted) setShowDisclaimerError(false);
  }, [disclaimerAccepted]);

  const placeReturn = () => {
    setErrorMessage('');
    setIsLoading(true);

    var returnData = getReturnData(itemsToReturn);

    // if return data is not valid
    if (!returnData) {
      setErrorMessage(Translations.InputValidationMessage);
      setIsLoading(false);
      return;
    }

    // if there are no order lines chosen
    if (returnData.noOrderLinesSelected) {
      setErrorMessage(Translations.NoLinesSelectedMessage);
      setIsLoading(false);
      return;
    }

    if (!disclaimerAccepted) {
      setShowDisclaimerError(true);
      setIsLoading(false);
      return;
    }

    const url = '/api/sitecore/returns/CreateRMA';
    const options = {
      method: 'POST',
      body: JSON.stringify(returnData),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    };

    try {
      fetchPromise(url, options)
        .then((response) => {
          if (response.Success) {
            setReturnData(response);
            setReturnDeliveryMethodDescription(returnData.LinesToReturn);
            sendAnalyticsForReturn(response.Lines);
            setReturnCompleted(true);
            props.scrollToTop();
          } else if (response.Messages.length > 0 && response.Messages[0].Content) {
            setErrorMessage(response.Messages[0].Content);
          } else {
            setErrorMessage(Translations.GeneralErrorMessage);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    } catch (err) {
      console.log('error in post request');
      console.log(err);
      setIsLoading(false);
    }
  };

  const setReturnDeliveryMethodDescription = (orderLines) => {
    if (!orderLines || orderLines.length === 0) return;

    const allHome = orderLines.every((line) => line.ShipMethodIsHomeDelivery);
    const pickup = orderLines.every((line) => !line.ShipMethodIsHomeDelivery);

    if (allHome) {
      setReturnDescription(Translations.ReturnOrderHomeDeliveryDescription);
    } else if (pickup) {
      setReturnDescription(Translations.ReturnOrderResultDescription);
    } else setReturnDescription(Translations.ReturnOrderMixedDeliveryDescription);
  };

  const getReturnData = (linesToReturn) => {
    const validated = validateLines(linesToReturn);
    if (!validated) return;

    var orderLinesToReturn = linesToReturn.map((t) => ({
      LineId: t.LineId,
      Quantity: t.QuantityToReturn,
      ShipMethodIsHomeDelivery: t.ShipMethodIsHomeDelivery,
    }));

    const reasonsForReturn = linesToReturn.reduce(
      (reasons, currentLine) => reasons + `${currentLine.LineId}:${currentLine.ReturnReason};`,
      ''
    );

    if (orderLinesToReturn && reasonsForReturn) {
      // if orderlines and reason for returns are valid
      var returnData = {
        OrderId: currentOrder.OrderId,
        LinesToReturn: orderLinesToReturn,
        ReturnReason: reasonsForReturn,
        ShopName: currentOrder.ShopName,
        UserId: currentOrder.UserId,
        CustomerId: currentOrder.CustomerId,
      };

      return returnData;
    } else if (orderLinesToReturn.length === 0) {
      // if no orderlines are selected
      return { noOrderLinesSelected: true };
    }

    // if orderlines or reason for returns are invalid
    return false;
  };

  const validateLines = (linesToReturn) => {
    let validated = true;
    let allLines = [...orderLines];

    linesToReturn.forEach((ol) => {
      const quantityValidation = validateQuantity(ol.QuantityToReturn);
      const reasonValidation = validateReturnReason(ol.ReturnReason);
      if (!quantityValidation || !reasonValidation) validated = false;

      const returnLineIndex = allLines.findIndex((r) => r.LineId === ol.LineId);
      let line = { ...allLines[returnLineIndex] };

      line.QuantityError = !quantityValidation;
      line.ReturnReasonError = !reasonValidation;
      allLines[returnLineIndex] = line;
    });
    setOrderLines(allLines);
    return validated;
  };

  const validateQuantity = (quantity) => {
    return !isNaN(quantity);
  };

  const validateReturnReason = (reason) => {
    return reason?.length > 0;
  };

  const sendAnalyticsForReturn = (lines) => {
    var orderId = currentOrder.OrderId;
    var products = lines.map(function (line) {
      return {
        id: line.Sku,
        quantity: line.Quantity,
      };
    });
    // window.google.tagmanager.events.placeAReturn(orderId, products); //todo fix tagmanager
  };

  return (
    <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 mb-14">
      <>
        {returnCompleted ? (
          <div id="return-result">
            <p className="paragraph--s text-color--gray80 mb-3">{Translations.OrderDetailsOrderNumberLabel}:</p>
            <p className="paragraph text-color--pitchBlack mb-5">{currentOrder.OrderConfirmationId}</p>
            <p className="paragraph--s text-color--gray80 mb-3">{Translations.ReturnDate}:</p>
            <p className="paragraph text-color--pitchBlack mb-5">
              {new Date().toLocaleDateString(window._THULEDATA.SitecoreLanguage)}
            </p>
            <h1 className="heading2 mb-9">{Translations.ReturnOrderResultHeader}</h1>
            <div className="instructions mb-9" dangerouslySetInnerHTML={createMarkup(returnDescription)} />
            {returnData?.Lines?.length > 0 && (
              <section className="orderlines mb-7 mb-lg-9">
                {returnData.Lines.map((ol, i) => (
                  <Orderline {...props} ol={ol} i={i} isReturned={true} key={ol.LineId} />
                ))}
              </section>
            )}
            <div className="return-summary p-8">
              <h2 className="heading4 mb-7 text-color--pitchBlack">{Translations.ReturnSummary}</h2>
              <div className="order-info-row mb-3">
                <span className="paragraph--s text-color--pitchBlack">{Translations.ReturnedProducts}</span>
                <span className="paragraph--s text-color--gray80">{returnData.ReturnProducts}</span>
              </div>
              {returnData.OriginalFreight && (
                <div className="order-info-row mb-3">
                  <span className="paragraph--s text-color--pitchBlack">{Translations.OriginalShipping}</span>
                  <span className="paragraph--s text-color--gray80">{returnData.OriginalFreight}</span>
                </div>
              )}
              {returnData.ReturnFreight && (
                <div className="order-info-row mb-5">
                  <span className="paragraph--s text-color--pitchBlack">{Translations.ReturnFreight}</span>
                  <span className="paragraph--s text-color--gray80">{returnData.ReturnFreight}</span>
                </div>
              )}
              {returnData.ReturnTaxTotal && (
                <div className="order-info-row mb-5">
                  <span className="paragraph--s text-color--pitchBlack">{Translations.Tax}</span>
                  <span className="paragraph--s text-color--gray80">{returnData.ReturnTaxTotal}</span>
                </div>
              )}
              <hr />
              <div className="order-info-row">
                <span className="heading5">{Translations.ReturnTotal}</span>
                <span className="heading5">{returnData.ReturnTotal}</span>
              </div>
            </div>
          </div>
        ) : (
          <div id="return-form">
            <div>
              <h1 className="heading2 mb-8">{Translations.Heading}</h1>
              <div
                className="page-intro-text mb-12"
                dangerouslySetInnerHTML={createMarkup(Translations.ReturnOrderDescription)}
              />
              <OrderDetails {...props} />
              {infoMessage && (
                <div className="mb-5">
                  <Notification
                    Type={'information'}
                    Size={'lg'}
                    Message={infoMessage}
                    HideCloseBtn={true}
                    ShowIcon={true}
                  />
                </div>
              )}
            </div>
            <section className="orderlines mb-7 mb-lg-9">
              {orderLines.map((ol, i) => {
                return (
                  <Orderline
                    {...props}
                    ol={ol}
                    i={i}
                    isReturned={false}
                    setOrderLines={setOrderLines}
                    orderLines={orderLines}
                    key={ol.LineId}
                  />
                );
              })}
            </section>
            <Checkbox
              id="disclaimerCheckbox"
              onChange={() => setDisclaimerAccepted(!disclaimerAccepted)}
              label={Translations.ReturnOrderDisclaimer}
              checked={disclaimerAccepted}
              errorMessage={showDisclaimerError ? Translations.ReturnPolicyValidationMessage : ''}
            />
            {errorMessage && (
              <div className="mt-5">
                <Notification
                  Type={'error'}
                  Size={'lg'}
                  Title={Translations.GeneralErrorHeader}
                  Message={errorMessage}
                  HideCloseBtn={true}
                  ShowIcon={true}
                />
              </div>
            )}
            <button
              onClick={(e) => placeReturn(e)}
              className="btn thule-btn-default w-100 my-5"
              disabled={isLoading || itemsToReturn?.length === 0 || !disclaimerAccepted}
            >
              {Translations.ReturnOrderButton}
              {isLoading && <Spinner />}
            </button>
            {itemsToReturn?.length > 0 && (
              <button
                className="btn thule-btn-secondary w-100"
                onClick={() => setOrderLines(currentOrder.OrderLines)}
                disabled={isLoading}
              >
                {Translations.Reset} ( {itemsToReturn?.length} )
              </button>
            )}
          </div>
        )}
      </>
    </div>
  );
};
